/**
 * @kgc/partners — partner/02-03 admin-szerkesztő SEAM (D0 frozen kontraktus)
 *
 * ⚠️ FRONTEND-SAFE MODUL — kizárólag `zod` + tiszta dto-import. NE importálj
 * ide NestJS-t (repository/service réteg), különben a `@kgc/partners/partner-editor`
 * sub-path import NestJS-t húz a FE-bundle-be. A package barrel (`index.ts`)
 * NestJS-szennyezett (repositories/services), ezért a FE EZT a sub-path-ot importálja:
 *   import { PartnerEditorReadSchema } from '@kgc/partners/partner-editor';
 *
 * Wire-kontraktus a meglévő `partners-direct` controllerhez:
 *   GET   /api/v1/partners-direct/:id  → { data: PartnerEditorRead }
 *   PATCH /api/v1/partners-direct/:id  ← PartnerEditorPatch
 *
 * 🔑 ENUM READ/WRITE ASZIMMETRIA (kritikus, a controller toDTO() mappel):
 *   - READ wire: kisbetűs — ACTIVE→'active', BLACKLISTED→'blocked' (NEM 'blacklisted'!)
 *   - PATCH wire: NAGYBETŰS — a meglévő PartnerTypeSchema-t reuse-oljuk
 *   A FE NEM küldheti vissza nyersen amit kapott (read-status ≠ patch-status).
 *
 * 🔒 idCardNumber GDPR: READ = maskedIdCardNumber ('****XXXX'); PATCH = nyers érték,
 *   üres/elhagyott = BE megtartja a régit (NEM null-olható).
 *
 * 🚫 status-váltás NEM ezen a seam-en megy — dedikált endpoint (UpdatePartnerStatusSchema,
 *   WARNING-refinementtel). Ez a seam mező-szerkesztés (field PATCH).
 */

import { z } from 'zod';
import { PartnerTypeSchema } from './partner.dto';

// ============================================
// WIRE-ENUM (READ — kisbetűs, controller-mappelt)
// ============================================

/** READ-oldali partner-típus (BE toDTO kisbetűsít) */
export const PartnerTypeWireSchema = z.enum(['individual', 'company']);

/** READ-oldali státusz (BE toDTO: BLACKLISTED→'blocked') */
export const PartnerStatusWireSchema = z.enum([
  'active',
  'inactive',
  'blocked',
  'trusted',
  'warning',
]);

/** ISO date-only wire-formátum (YYYY-MM-DD) — a Prisma @db.Date mezők */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'ISO dátum kell (YYYY-MM-DD)',
});

// ============================================
// READ — GET /api/v1/partners-direct/:id → data
// ============================================

const PartnerPhoneReadSchema = z.object({
  id: z.string().uuid(),
  label: z.string().nullable(),
  number: z.string(),
  isPrimary: z.boolean(),
});

const PartnerAddressReadSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  isDefault: z.boolean(),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  street: z.string().nullable(),
});

const PartnerContactReadSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  position: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  isPrimary: z.boolean(),
});

const PartnerStatsReadSchema = z.object({
  totalOrders: z.number(),
  totalRevenue: z.number(),
  totalRentals: z.number(),
  totalServiceOrders: z.number(),
  outstandingBalance: z.number(),
  /** régi PartnerDetailPage compat — detail endpoint stats-ból jön, lista-endpointnál undefined */
  lastOrderDate: z.string().nullable().optional(),
  /** régi PartnerDetailPage compat — detail endpoint stats-ból jön, lista-endpointnál undefined */
  lastRentalDate: z.string().nullable().optional(),
});

export const PartnerEditorReadSchema = z.object({
  id: z.string().uuid(),
  /** = partnerCode (BE mappelt mezőnév) */
  code: z.string(),
  type: PartnerTypeWireSchema,
  status: PartnerStatusWireSchema,
  name: z.string(),
  companyName: z.string().nullable(),
  contactName: z.string().nullable(),
  taxNumber: z.string().nullable(),
  euVatNumber: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  phoneAlt: z.string().nullable(),
  notificationEmail: z.string().nullable(),
  billingEmail: z.string().nullable(),
  country: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  address: z.string().nullable(),
  addressAlt: z.string().nullable(),
  birthDate: z.string().nullable(),
  birthPlace: z.string().nullable(),
  mothersName: z.string().nullable(),
  /** GDPR: maszkolt forma ('****XXXX') — a nyers érték sosem hagyja el a BE-t */
  maskedIdCardNumber: z.string().nullable(),
  idCardExpiry: z.string().nullable(),
  temporaryAddress: z.string().nullable(),
  tempAddressValidUntil: z.string().nullable(),
  lastDocumentScanDate: z.string().nullable(),
  creditLimit: z.number().nullable(),
  /** read-only (BE számolt egyenleg) */
  currentBalance: z.number(),
  paymentTermDays: z.number(),
  defaultDiscountPc: z.number(),
  discountRental: z.number().nullable(),
  discountSales: z.number().nullable(),
  discountService: z.number().nullable(),
  /** read-only (E-90 loyalty) */
  loyaltyPoints: z.number(),
  warningNote: z.string().nullable(),
  notes: z.string().nullable(),
  costBearerId: z.string().uuid().nullable(),
  costBearerName: z.string().nullable(),
  /** read-only (E-90 számított szegmens: VIP|ACTIVE|AT_RISK|INACTIVE) */
  segment: z.string(),
  /** read-only (E-90: NONE|THREE_MONTH|SIX_MONTH|PERMANENT) */
  transferBanLevel: z.string(),
  /** read-only (E-90 abuse-guard) */
  bookingRestricted: z.boolean(),
  paymentBypassEnabled: z.boolean(),
  idVerified: z.boolean(),
  phones: z.array(PartnerPhoneReadSchema),
  addresses: z.array(PartnerAddressReadSchema),
  contacts: z.array(PartnerContactReadSchema),
  stats: PartnerStatsReadSchema,
  /** FE-oldali enrichment (kategória-cimkék) */
  categories: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  // ============================================
  // S2b AMENDMENT — superset mezők (optional, mert a lista-endpoint nem adja)
  // ============================================
  /**
   * Kaució mentesség — Prisma DepositExemption enum skalár.
   * Értékek: 'REQUIRED' | 'EXEMPT' | 'AUTOMATIC' (partner.prisma Story 78-5).
   */
  depositExemption: z.enum(['REQUIRED', 'EXEMPT', 'AUTOMATIC']).nullable().optional(),
  /**
   * Törzsvendég szint (nested { name }) — loyaltyTier.tierName mappelve.
   * null ha nincs hozzárendelve szint.
   */
  loyaltyTier: z.object({ name: z.string() }).nullable().optional(),
  /**
   * Nested cost-bearer { id, name } — a régi PartnerDetailPage partner.costBearer!.id-t URL-be teszi.
   * Párhuzamosan él a flat costBearerId + costBearerName mezőkkel (EditPage/PATCH).
   */
  costBearer: z.object({ id: z.string().uuid(), name: z.string() }).nullable().optional(),
  /**
   * Azok a partnerek akiknek ez a partner a cost-bearer-je (cégnél dolgozó magánszemélyek).
   */
  costBearerClients: z.array(z.object({ id: z.string().uuid(), name: z.string() })).optional(),
  /**
   * Bérlés-tiltás (computed) — true ha status === 'BLACKLISTED'.
   * Nincs Prisma-skalár, toDTO() számolja.
   */
  rentalBlocked: z.boolean().optional(),
});

export type PartnerEditorRead = z.infer<typeof PartnerEditorReadSchema>;

// ============================================
// PATCH — PATCH /api/v1/partners-direct/:id
// (mind optional — csak a küldött mezők változnak; M-017 konvenció)
// ============================================

export const PartnerEditorPatchSchema = z.object({
  /** NAGYBETŰS wire (reuse a kanonikus partner.dto enum-ból) */
  type: PartnerTypeSchema.optional(),
  name: z.string().min(1).max(255).optional(),
  companyName: z.string().max(255).nullable().optional(),
  contactName: z.string().max(255).nullable().optional(),
  taxNumber: z.string().max(20).nullable().optional(),
  euVatNumber: z.string().max(20).nullable().optional(),
  email: z.string().email().nullable().optional(),
  notificationEmail: z.string().email().nullable().optional(),
  billingEmail: z.string().email().nullable().optional(),
  phone: z.string().max(30).nullable().optional(),
  phoneAlt: z.string().max(30).nullable().optional(),
  country: z.string().length(2).nullable().optional(),
  postalCode: z.string().max(20).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  address: z.string().max(255).nullable().optional(),
  addressAlt: z.string().max(255).nullable().optional(),
  birthDate: isoDate.nullable().optional(),
  birthPlace: z.string().max(100).nullable().optional(),
  mothersName: z.string().max(255).nullable().optional(),
  /** GDPR: nyers érték; üres/elhagyott = BE megtartja → NEM nullable */
  idCardNumber: z.string().max(50).optional(),
  idCardExpiry: isoDate.nullable().optional(),
  temporaryAddress: z.string().max(500).nullable().optional(),
  tempAddressValidUntil: isoDate.nullable().optional(),
  lastDocumentScanDate: isoDate.nullable().optional(),
  creditLimit: z.number().int().min(0).nullable().optional(), // M-021 HUF integer
  paymentTermDays: z.number().int().min(0).max(365).optional(),
  defaultDiscountPc: z.number().int().min(0).max(100).optional(),
  discountRental: z.number().int().min(0).max(15).optional(),
  discountSales: z.number().int().min(0).max(15).optional(),
  discountService: z.number().int().min(0).max(15).optional(),
  notes: z.string().nullable().optional(),
  warningNote: z.string().max(500).nullable().optional(),
  idVerified: z.boolean().optional(),
  paymentBypassEnabled: z.boolean().optional(),
});

export type PartnerEditorPatch = z.infer<typeof PartnerEditorPatchSchema>;
